/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * A FIRM'S TRIAL END DATE (v1_884).
 *
 * The platform owner's date box accepted any year it was given. A two digit
 * year typed into it ("26") was stored as the year 0026; the dashboard then
 * showed "30/11/26", which reads as 2026, while the server compared 0026 with
 * today, found the trial long over and paused the firm's invitations (Wincent).
 *
 * A trial end is a real calendar date, from today to five years ahead. The
 * screen and the server both check it, and a stored date is always shown with
 * its full year (dd/mm/yyyy), so a wrong year can never look right.
 */

export const TRIAL_MAX_YEARS_AHEAD = 5;

const pad = (n, w) => String(n).padStart(w, '0');

// dd/mm/yyyy with the full year, whatever the year is (0026 shows as 0026).
export function fullUkDate(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return pad(d.getUTCDate(), 2) + '/' + pad(d.getUTCMonth() + 1, 2) + '/' + pad(d.getUTCFullYear(), 4);
}

// Check a yyyy-mm-dd from the date box, or a stored timestamp. Returns
// { ok: true, iso } (end of that day, UTC) or { ok: false, error }.
export function checkTrialEnd(value, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!m) return { ok: false, error: 'The trial end is not a date. Choose it again in the date box.' };
  const y = +m[1], mo = +m[2], d = +m[3];
  const dt = new Date(Date.UTC(y, mo - 1, d, 23, 59, 59));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    return { ok: false, error: 'The trial end ' + pad(d, 2) + '/' + pad(mo, 2) + '/' + pad(y, 4) + ' is not a real date.' };
  }
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const latest = Date.UTC(now.getUTCFullYear() + TRIAL_MAX_YEARS_AHEAD, now.getUTCMonth(), now.getUTCDate(), 23, 59, 59);
  if (dt.getTime() < today) {
    return { ok: false, error: 'The trial end ' + fullUkDate(dt) + ' is in the past, so the trial would end at once and new invitations would pause. Check the year (it must be written in full, for example 2026).' };
  }
  if (dt.getTime() > latest) {
    return { ok: false, error: 'The trial end ' + fullUkDate(dt) + ' is more than ' + TRIAL_MAX_YEARS_AHEAD + ' years away. Check the year.' };
  }
  return { ok: true, iso: dt.toISOString() };
}

// A stored trial end that could not have been entered on purpose (before the
// year 2000), shown to the platform owner so it is set again.
export function storedTrialEndImpossible(value) {
  if (!value) return false;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) || d.getUTCFullYear() < 2000;
}
